import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import * as RadixAccordion from "@radix-ui/react-accordion";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type FAQBlockViewProps<TItem> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemValue: (item: TItem) => string;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  items?: TItem[] | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode?: string | null;
  renderQuestion: (item: TItem, isOpen: boolean) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  /** Mirror the open question into `location.hash`. Off for isolated harnesses. */
  syncHash?: boolean;
  theme?: string | null;
};
export function FAQBlockView<TItem>({
  anchor,
  getItemValue,
  hasTopDivider = true,
  headline,
  items = [],
  link,
  mode = "light",
  renderQuestion,
  sectionProps,
  syncHash = true,
  theme = "white"
}: FAQBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // getThemeStyle maps the "white" theme to grey-20 (#efeff6); render it as true
  // white here (matching textMedia1_2) so a white-themed FAQ is #ffffff, not lavender.
  // bg-primary is mode-aware (white in light, near-black in dark) so dark-mode FAQs stay legible.
  const background = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  const [activeQuestionId, setActiveQuestionId] = React.useState("");
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  const questions = React.useMemo(() => items ?? [], [items]);
  const ownedValues = React.useMemo(() => new Set(questions.map(question => getItemValue(question)).filter(Boolean)),
  // `getItemValue` is defined inline by every caller, so keying on it would
  // rebuild this set on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [questions]);

  // Deep link in: open (and correct the scroll for) the question named by the
  // hash. The browser has already jumped to the anchor by itself — the answer is
  // in the server HTML — so this only accounts for the newly expanded height.
  React.useEffect(() => {
    if (!syncHash) {
      return undefined;
    }
    const openFromHash = (behavior: ScrollBehavior) => {
      const slug = decodeURIComponent(window.location.hash.slice(1));
      if (!slug || !ownedValues.has(slug)) {
        return;
      }
      setActiveQuestionId(slug);
      window.requestAnimationFrame(() => {
        document.getElementById(slug)?.scrollIntoView({
          block: "start",
          behavior
        });
      });
    };

    // Instant on a cold load, matching what a plain anchor navigation does.
    openFromHash("auto");
    const onHashChange = () => openFromHash("smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [ownedValues, syncHash]);

  // Expanding a question appends its anchor to the URL so it can be linked and
  // cited directly. `replaceState` rather than the Next router: a route change
  // would re-render the tree and fight Next's scroll restoration, and rather
  // than `pushState` so expanding five questions does not add five history
  // entries the back button has to walk through.
  React.useEffect(() => {
    if (!syncHash) {
      return;
    }
    const desiredHash = activeQuestionId && ownedValues.has(activeQuestionId) ? `#${activeQuestionId}` : "";

    // Another FAQ section on this page may own the current hash; only clear a
    // hash that is ours. Bailing when it already matches is also what stops the
    // read effect and this one from looping.
    if (!desiredHash && !ownedValues.has(decodeURIComponent(window.location.hash.slice(1)))) {
      return;
    }
    if (desiredHash === window.location.hash) {
      return;
    }
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${desiredHash}`);
  }, [activeQuestionId, ownedValues, syncHash]);
  if (!hasItems(questions)) {
    return null;
  }
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, background, themeStyle.border]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv $_css2={[{
        "position": "relative",
        "zIndex": "10",
        "@media (min-width: 1024px)": {
          "display": "flex"
        }
      }, background]}>
          <_StyledDiv2 $_css3={[{
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "flex-start",
          "gap": "2.4rem",
          "borderBottomWidth": "1px",
          "padding": "4rem",
          "@media (min-width: 1024px)": {
            "width": "50%",
            "gap": "4rem",
            "borderBottomWidth": "0px",
            "padding": "8rem"
          }
        }, themeStyle.border]}>
            {headline && <_StyledHeadline type="h2" text={headline} />}
            {isLinkValid(cta) && <Button as={Link} {...cta} variant="secondary" icon={icon} />}
          </_StyledDiv2>

          <_StyledRadixAccordionRoot value={activeQuestionId} onValueChange={id => setActiveQuestionId(id)} collapsible type="single" $_css4={[{
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          },
          "@media (min-width: 1024px)": {
            "width": "50%",
            "borderLeftWidth": "1px"
          }
        }, themeStyle.border]}>
            {questions.map((question, idx) => <React.Fragment key={getItemValue(question) || idx}>
                {renderQuestion(question, getItemValue(question) === activeQuestionId)}
              </React.Fragment>)}
          </_StyledRadixAccordionRoot>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-17wimhf-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-17wimhf-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-17wimhf-2"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled4(Headline).withConfig({
  componentId: "sc-sf-17wimhf-3"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledRadixAccordionRoot = /*#__PURE__*/_styled5(RadixAccordion.Root).withConfig({
  displayName: "faq___StyledRadixAccordionRoot",
  componentId: "sc-x32ilh-0"
})(["", ""], p => p.$_css4);